import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import Swal from 'sweetalert2';
import { DentistService } from '../../../../services/dentist';

@Component({
  selector: 'app-dentistform',
  templateUrl: './dentistform.component.html',
  styleUrl: './dentistform.component.scss'
})
export class DentistformComponent implements OnInit {

  data!: FormGroup;
  contentReady = false;

  isUpdatePage = false;
  editId!: number;

  selectedDoctorImage: File | null = null;
  existingDoctorImage = false;

  selectedCertificates: string[] = [];

  selectedCvFile: File | null = null;
  existingCv = false;

  selectedCertificateFiles: File[] = [];
  existingCertificateFiles: any[] = [];

  private readonly maxCertificateFilesCount = 20;
  private readonly maxCertificateFileSize = 5 * 1024 * 1024;

  specialties = [
    { value: 'General Dentistry', label: 'طب الأسنان العام' },
    { value: 'Family Dentistry', label: 'طب أسنان الأسرة' },
    { value: 'Endodontics', label: 'علاج الجذور' },
    { value: 'Prosthodontics', label: 'الاستعاضة السنية' },
    { value: 'Periodontics', label: 'علاج اللثة' },
    { value: 'Orthodontics', label: 'تقويم الأسنان' },
    { value: 'Oral & Maxillofacial Surgery (OMFS)', label: 'جراحة الفم والوجه والفكين' },
    { value: 'Oral Surgery', label: 'جراحة الفم' },
    { value: 'Pediatric Dentistry', label: 'طب أسنان الأطفال' },
    { value: 'Restorative Dentistry', label: 'طب الأسنان الترميمي' },
    { value: 'Oral Medicine / Oral Pathology', label: 'طب وأمراض الفم' },
    { value: 'Other', label: 'أخرى' }
  ];

  qualifications = [
    { value: 'Bachelor Degree (BDS / DDS)', label: 'بكالوريوس طب الأسنان' },
    { value: 'Master’s Degree', label: 'ماجستير' },
    { value: 'Fellowship/Board Certification', label: 'زمالة أو بورد' },
    { value: 'PhD', label: 'دكتوراه' },
    { value: 'PhD + Fellowship', label: 'دكتوراه + زمالة' },
    { value: 'Other', label: 'أخرى' }
  ];

  certificates = [
    { value: 'Endodontics', label: 'علاج الجذور' },
    { value: 'Fixed Prosthodontics', label: 'تركيبات ثابتة' },
    { value: 'Implant Prosthodontics', label: 'تركيبات زراعة الأسنان' },
    { value: 'Implant Surgery', label: 'جراحة زراعة الأسنان' },
    { value: 'Digital Dentistry', label: 'طب الأسنان الرقمي' },
    { value: 'Esthetic Dentistry', label: 'تجميل الأسنان' }
  ];

  scfhsLevels = [
    { value: 'General Dentist', label: 'طبيب أسنان عام' },
    { value: 'Registrar', label: 'أخصائي' },
    { value: 'Resident', label: 'طبيب مقيم' },
    { value: 'Senior Registrar', label: 'أخصائي أول' },
    { value: 'Consultant', label: 'استشاري' }
  ];

  constructor(
    private fb: FormBuilder,
    private dentistService: DentistService,
    private router: Router,
    private route: ActivatedRoute
  ) { }

  ngOnInit(): void {
    this.initForm();

    this.route.queryParams.subscribe(params => {
      if (params['id']) {
        this.isUpdatePage = true;
        this.editId = +params['id'];
        this.loadForEdit(this.editId);
      } else {
        this.contentReady = true;
      }
    });
  }

  initForm(): void {
    this.data = this.fb.group({
      full_name: ['', Validators.required],
      date_of_birth: [''],
      gender: [''],
      mobile: [''],
      email: ['', Validators.email],
      whatsapp_number: [''],

      specialty: ['', Validators.required],
      specialty_other: [''],

      highest_qualification: ['', Validators.required],

      master_year: [null, [Validators.min(1940), Validators.max(2100)]],
      phd_year: [null, [Validators.min(1940), Validators.max(2100)]],

      has_additional_certificates: [false],
      additional_certificates: [[]],

      scfhs_status: [''],
      scfhs_classification_level: [''],
      scfhs_classification_date: [''],

      years_of_practice: [''],

      preferred_employment_type: [''],
      preferred_practice_environment: [''],

      portfolio_link: [''],

      additional_notes: ['']
    });

    this.data.get('has_additional_certificates')?.valueChanges.subscribe(value => {
      if (!value) {
        this.selectedCertificates = [];
        this.data.patchValue({ additional_certificates: [] }, { emitEvent: false });
      }
    });

    this.data.get('scfhs_status')?.valueChanges.subscribe(value => {
      if (value === 'no') {
        this.data.patchValue({
          scfhs_classification_level: '',
          scfhs_classification_date: ''
        }, { emitEvent: false });
      }
    });

    this.data.get('specialty')?.valueChanges.subscribe(() => {
      if (!this.isSurgerySpecialty()) {
        this.data.patchValue({
          preferred_practice_environment: ''
        }, { emitEvent: false });
      }
    });
  }

  loadForEdit(id: number): void {
    this.contentReady = false;

    this.dentistService.getById(id).subscribe({
      next: (res: any) => {
        const item = res?.data || res;

        this.data.patchValue({
          ...item,
          scfhs_classification_level: this.normalizeScfhsLevel(item?.scfhs_classification_level)
        });

        this.selectedCertificates = this.normalizeCertificates(item?.additional_certificates);

        this.data.patchValue({
          additional_certificates: [...this.selectedCertificates],
          has_additional_certificates: item?.has_additional_certificates ? true : false
        });

        this.existingCv = !!item?.cv_pdf;
        this.existingDoctorImage = !!item?.doctor_image || !!item?.doctor_image_url;

        this.existingCertificateFiles = Array.isArray(item?.certificates)
          ? item.certificates
          : [];

        this.contentReady = true;
      },
      error: () => {
        Swal.fire('خطأ', 'فشل تحميل البيانات', 'error');
        this.contentReady = true;
      }
    });
  }

  onDoctorImageSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] || null;

    if (!file) return;

    if (
      file.type === 'image/jpeg' ||
      file.type === 'image/png' ||
      file.type === 'image/jpg'
    ) {
      this.selectedDoctorImage = file;
      return;
    }

    Swal.fire('خطأ', 'يجب أن تكون الصورة بصيغة JPG أو PNG', 'error');
    input.value = '';
    this.selectedDoctorImage = null;
  }

  onCvSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] || null;

    if (!file) return;

    if (file.type === 'application/pdf') {
      this.selectedCvFile = file;
      return;
    }

    Swal.fire('خطأ', 'يجب أن تكون السيرة الذاتية ملف PDF', 'error');
    input.value = '';
    this.selectedCvFile = null;
  }

  onCertificateFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files: File[] = Array.from(input.files || []);

    if (!files.length) return;

    const currentTotal =
      this.selectedCertificateFiles.length +
      this.existingCertificateFiles.length +
      files.length;

    if (currentTotal > this.maxCertificateFilesCount) {
      Swal.fire(
        'خطأ',
        `لا يمكن رفع أكثر من ${this.maxCertificateFilesCount} ملف شهادة`,
        'error'
      );

      input.value = '';
      return;
    }

    for (const file of files) {
      if (!this.isValidCertificateFile(file)) {
        Swal.fire(
          'خطأ',
          'ملفات الشهادات يجب أن تكون صورة أو PDF فقط',
          'error'
        );

        input.value = '';
        return;
      }

      if (file.size > this.maxCertificateFileSize) {
        Swal.fire(
          'خطأ',
          'حجم كل ملف شهادة يجب ألا يزيد عن 5 ميجا',
          'error'
        );

        input.value = '';
        return;
      }
    }

    this.selectedCertificateFiles = [
      ...this.selectedCertificateFiles,
      ...files
    ];

    input.value = '';
  }

  removeSelectedCertificateFile(index: number): void {
    this.selectedCertificateFiles = this.selectedCertificateFiles.filter(
      (_, i) => i !== index
    );
  }

  toggleCertificate(value: string, event: Event): void {
    const input = event.target as HTMLInputElement;

    if (input.checked) {
      if (!this.selectedCertificates.includes(value)) {
        this.selectedCertificates.push(value);
      }
    } else {
      this.selectedCertificates = this.selectedCertificates.filter(
        v => v !== value
      );
    }

    this.data.patchValue({
      additional_certificates: [...this.selectedCertificates]
    });
  }

  isSurgerySpecialty(): boolean {
    const s = this.data?.value?.specialty;

    return (
      s === 'Oral Surgery' ||
      s === 'Oral & Maxillofacial Surgery (OMFS)'
    );
  }

  buildFormData(): FormData {
    const fd = new FormData();

    Object.entries(this.data.value).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach(v => fd.append(`${key}[]`, String(v)));
        return;
      }

      if (typeof value === 'boolean') {
        fd.append(key, value ? '1' : '0');
        return;
      }

      if (value !== null && value !== undefined && value !== '') {
        fd.append(key, String(value));
      }
    });

    if (this.selectedCvFile) {
      fd.append('cv_pdf', this.selectedCvFile);
    }

    if (this.selectedDoctorImage) {
      fd.append('doctor_image', this.selectedDoctorImage);
    }

    this.selectedCertificateFiles.forEach(file => {
      fd.append('certificates[]', file);
    });

    return fd;
  }

  onSubmit(): void {
    if (this.data.invalid) {
      this.data.markAllAsTouched();
      Swal.fire('تنبيه', 'من فضلك راجع البيانات المطلوبة', 'warning');
      return;
    }

    const payload = this.buildFormData();
    this.contentReady = false;

    this.isUpdatePage
      ? this.onUpdate(payload)
      : this.onCreate(payload);
  }

  onCreate(payload: FormData): void {
    this.dentistService.create(payload).subscribe({
      next: () => {
        Swal.fire('تم بنجاح', 'تم إنشاء التسجيل بنجاح', 'success');
        this.router.navigateByUrl('/alldentist');
      },
      error: err => {
        Swal.fire(
          'خطأ',
          err.error?.message || err.error?.error || 'فشل إنشاء التسجيل',
          'error'
        );

        this.contentReady = true;
      }
    });
  }

  onUpdate(payload: FormData): void {
    this.dentistService.update(this.editId, payload).subscribe({
      next: () => {
        Swal.fire('تم بنجاح', 'تم تحديث التسجيل بنجاح', 'success');
        this.router.navigateByUrl('/alldentist');
      },
      error: err => {
        Swal.fire(
          'خطأ',
          err.error?.message || err.error?.error || 'فشل تحديث التسجيل',
          'error'
        );

        this.contentReady = true;
      }
    });
  }

  getCertificateUrl(cert: any): string {
    if (cert?.url) return cert.url;

    const path = cert?.file_path;

    if (!path) return '';

    if (path.startsWith('http://') || path.startsWith('https://')) {
      return path;
    }

    return `/storage/${path}`;
  }

  isInvalid(controlName: string): boolean {
    const control = this.data.get(controlName);
    return Boolean(control?.invalid && (control.dirty || control.touched));
  }

  private isValidCertificateFile(file: File): boolean {
    const name = file.name.toLowerCase();

    return (
      file.type.startsWith('image/') ||
      file.type === 'application/pdf' ||
      name.endsWith('.jpg') ||
      name.endsWith('.jpeg') ||
      name.endsWith('.png') ||
      name.endsWith('.webp') ||
      name.endsWith('.pdf')
    );
  }

  private normalizeCertificates(value: any): string[] {
    if (!value) return [];

    if (Array.isArray(value)) {
      return value;
    }

    if (typeof value === 'string') {
      try {
        const parsed = JSON.parse(value);

        if (Array.isArray(parsed)) {
          return parsed;
        }

        return value
          .split(',')
          .map(item => item.trim())
          .filter(Boolean);
      } catch {
        return value
          .split(',')
          .map(item => item.trim())
          .filter(Boolean);
      }
    }

    return [];
  }

  private normalizeScfhsLevel(value: any): string {
    if (!value) return '';

    const normalized = String(value).trim().toLowerCase().replace(/[\s-]+/g, '_');

    const map: Record<string, string> = {
      general_dentist: 'General Dentist',
      registrar: 'Registrar',
      resident: 'Resident',
      senior_registrar: 'Senior Registrar',
      consultant: 'Consultant'
    };

    return map[normalized] || String(value).trim();
  }
}
